/**
 * Adaptive Batch-vs-Realtime LLM Router (Patent Sub-system J)
 * ============================================================
 * Demo the classifier: paste request signals, see whether we route to
 * provider's realtime or batch endpoint (50% off, 24h SLA).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Zap, Clock, Sparkles, Loader2, FlaskConical, CheckCircle2, ArrowRight,
  TrendingDown, Activity, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  if (n == null) return '$0';
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};

const PRESET_SCENARIOS = [
  {
    id: 'cron',
    label: 'Weekly digest cron · 12K tokens',
    payload: {
      caller_service: 'weekly-digest-cron',
      caller_context: 'weekly-digest-summarise-all',
      token_count: 12000,
      estimated_call_cost_usd: 0.045,
      provider: 'anthropic',
    },
  },
  {
    id: 'chat',
    label: 'Interactive chat (streaming) · 800 tokens',
    payload: {
      has_streaming_handler: true,
      caller_service: 'api-server',
      token_count: 800,
      estimated_call_cost_usd: 0.008,
      provider: 'openai',
    },
  },
  {
    id: 'sdk-declared',
    label: 'SDK declared 24h acceptable latency',
    payload: {
      acceptable_latency_hours: 24,
      caller_service: 'queue-worker',
      token_count: 2400,
      estimated_call_cost_usd: 0.018,
      provider: 'openai',
    },
  },
  {
    id: 'nightly',
    label: 'Off-peak backfill at 03:00 UTC · 18K tokens',
    payload: {
      caller_service: 'backfill-worker',
      caller_context: 'overnight-report-generation',
      token_count: 18000,
      request_hour_utc: 3,
      estimated_call_cost_usd: 0.072,
      provider: 'anthropic',
    },
  },
];

const BatchRouter = () => {
  const [signals, setSignals] = useState([]);
  const [discountProfile, setDiscountProfile] = useState({});
  const [scenario, setScenario] = useState(PRESET_SCENARIOS[0]);
  const [result, setResult] = useState(null);
  const [classifying, setClassifying] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/batch-router/signals`)
      .then((r) => {
        setSignals(r.data.signals || []);
        setDiscountProfile(r.data.batch_discount_profile || {});
      })
      .catch(() => {});
    // run an initial classify with the first preset to populate the right pane
    axios.post(`${API_URL}/batch-router/classify`, PRESET_SCENARIOS[0].payload)
      .then((r) => setResult(r.data))
      .catch(() => {});
  }, []);

  const runClassify = async (sc) => {
    setScenario(sc);
    setClassifying(true);
    setResult(null);
    try {
      const r = await axios.post(`${API_URL}/batch-router/classify`, sc.payload);
      setResult(r.data);
      toast.success(`Routed to ${r.data.routing.toUpperCase()}`);
    } catch (e) {
      toast.error('Classify failed');
    } finally {
      setClassifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="batch-router-page">
      <Helmet>
        <title>Adaptive Batch-vs-Realtime LLM Router | CARTIEAI</title>
        <meta name="description" content="The first LLM router that decides realtime-vs-batch per request and routes non-urgent traffic to provider's batch endpoint (50% off, 24h SLA)." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/30 rounded-md px-2 py-1 mb-5" data-testid="br-patent-badge">
            <FlaskConical className="w-3 h-3" />
            Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Adaptive Batch Router</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">
            Free money on the floor: OpenAI &amp; Anthropic Batch APIs are <span className="text-white font-semibold">50% off</span> (24h SLA). <span className="text-emerald-300">Nobody auto-routes there.</span> We do.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            For every LLM call, our classifier reads urgency signals from HTTP headers, calling service identity, streaming-handler presence, time-of-day, and request context — then routes urgent traffic to realtime, non-urgent to provider batch. Same SDK call, half the bill.
          </p>
        </div>

        {/* CLASSIFIER DEMO */}
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* LEFT — Scenario picker */}
          <Card className="lg:col-span-2 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-300" /> Try a scenario
              </CardTitle>
              <CardDescription>Each one is a realistic LLM call pattern. Pick → classifier runs.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {PRESET_SCENARIOS.map((sc) => {
                const active = scenario.id === sc.id;
                return (
                  <button
                    key={sc.id}
                    onClick={() => runClassify(sc)}
                    className={`w-full text-left px-3 py-2.5 rounded border transition ${active ? 'border-fuchsia-500/50 bg-emerald-500/10' : 'border-white/10 hover:bg-slate-950/60'}`}
                    data-testid={`br-scenario-${sc.id}`}
                  >
                    <div className="text-sm text-white">{sc.label}</div>
                    <div className="text-[10px] text-slate-500 mt-1 font-mono uppercase">
                      provider: {sc.payload.provider}
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* RIGHT — Classification result */}
          <Card className="lg:col-span-3 bg-slate-900/60 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-emerald-300" /> Routing decision
              </CardTitle>
              <CardDescription>Confidence + matched signals + projected savings.</CardDescription>
            </CardHeader>
            <CardContent>
              {classifying && (
                <div className="py-10 text-center text-slate-500 text-sm">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Classifying…
                </div>
              )}
              {result && !classifying && (
                <div data-testid="br-result">
                  <div className={`rounded-md border p-4 mb-4 ${result.routing === 'batch' ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-amber-500/40 bg-amber-500/10'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {result.routing === 'batch' ? <Clock className="w-5 h-5 text-emerald-300" /> : <Zap className="w-5 h-5 text-amber-300" />}
                        <span className="text-xs uppercase tracking-widest text-slate-400">Routed to</span>
                        <span className={`text-lg font-bold uppercase ${result.routing === 'batch' ? 'text-emerald-200' : 'text-amber-200'}`}>{result.routing}</span>
                      </div>
                      <Badge variant="outline" className="border-white/10 text-slate-400 font-mono">
                        confidence {(result.confidence * 100).toFixed(0)}%
                      </Badge>
                    </div>
                    <div className="text-sm text-slate-300 mt-3 leading-relaxed">{result.rationale}</div>
                    {result.routing === 'batch' && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="bg-slate-950 border border-white/5 rounded p-2 text-center">
                          <div className="text-[10px] uppercase text-slate-500">Savings on this call</div>
                          <div className="text-emerald-300 font-mono font-semibold">{fmtUsd(result.projected_savings_usd)}</div>
                        </div>
                        <div className="bg-slate-950 border border-white/5 rounded p-2 text-center">
                          <div className="text-[10px] uppercase text-slate-500">SLA</div>
                          <div className="text-emerald-300 font-mono font-semibold">{result.sla_hours}h</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Matched signals */}
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Matched signals</div>
                    {result.matched_signals.length === 0 && (
                      <div className="text-xs text-slate-300 italic">No specific signals matched — used default heuristics.</div>
                    )}
                    {result.matched_signals.map((s, idx) => (
                      <div key={idx} className="text-xs text-slate-400 mb-1.5 flex items-start gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span><span className="text-white font-medium">{s.id} · {s.name}</span> — {s.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SIGNAL CATALOG */}
        <div className="mt-16">
          <h2 className="text-base font-semibold tracking-widest text-emerald-300 uppercase mb-2">Classifier signals</h2>
          <p className="text-sm text-slate-400 mb-6 max-w-2xl">{signals.length} signals · ordered by decisiveness. Explicit headers + streaming-handler presence are decisive; everything else is heuristic-weighted.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="br-signal-catalog">
            {signals.map((s) => (
              <div key={s.id} className="bg-slate-900/60 border border-white/10 rounded-md p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] font-mono uppercase text-slate-500">{s.id}</span>
                  {s.decisive && <Badge variant="outline" className="text-[10px] border-fuchsia-400/40 text-emerald-300">decisive</Badge>}
                  <span className="text-[10px] font-mono text-slate-300">w={s.weight}</span>
                </div>
                <div className="text-white font-semibold text-sm">{s.name}</div>
                <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* PROVIDER PROFILE */}
        <Card className="mt-16 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 border-emerald-500/30">
          <CardContent className="p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-emerald-300 font-semibold mb-2">Batch endpoint catalog</div>
            <h3 className="text-2xl font-bold text-white">Three providers · same 50% discount · same 24h SLA.</h3>
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(discountProfile).map(([provider, p]) => (
                <div key={provider} className="bg-slate-950 border border-white/10 rounded p-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{provider}</div>
                  <div className="text-emerald-300 text-2xl font-bold mt-1">{(p.discount_pct * 100).toFixed(0)}% off</div>
                  <div className="text-xs text-slate-400 mt-1">{p.sla_hours}h SLA</div>
                  <div className="text-[10px] font-mono text-slate-300 mt-2 truncate">{p.endpoint}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BatchRouter;
